/**
 * Group key service — manages the lifecycle of symmetric group encryption keys.
 *
 * Pattern copied from spacelinq-android DefaultGroupKeysMessageHandler:
 *   - Group owner generates one AES-256 symmetric key per group
 *   - Owner distributes it to each member as a 1:1 ECDH-encrypted message
 *   - Members decrypt and store the key; all subsequent group messages use it
 *
 * Group key distribution wire format (sent as a 1:1 message to each member):
 *   JSON { cls: "group_key", group_name: string, content: string }
 *   where `content` = encryptMessage(JSON{ group_name, symmetric_key, group_owner_username })
 *   i.e. the same ECDH wire format used for 1:1 messages: IV:ciphertext:signature
 *
 * Matches Android's GroupKeyContentSerializable:
 *   { group_name, symmetric_key, group_owner_username }
 */

import {
  generateSymmetricKey,
  encryptMessage,
  decryptMessage,
} from "@/utils/crypto";
import {
  storeSymmetricKey,
  getSymmetricKey,
  getActiveKeySet,
  getPublicKeySetByUsername,
  PeerPublicKeySet,
} from "@/services/keyStorageService";
import { requestPublicKey } from "@/services/keyExchangeService";
import { sendUserMessage } from "@/services/conversationService";
import { getGroups } from "@/services/userGroupService";
import { GroupMember } from "@/types/users";
import { USER_ADDRESS_TYPE } from "@/constants/message";
import { E2EE_GROUP_ENABLED } from "@/constants/e2ee";

// cls field values — must match Android's ProtobufEncryptedGroupKeyMessageContentDetails.CLS
// and ProtobufGroupKeyRequestMessageContentDetails.CLS
export const GROUP_KEY_CLS = "encrypted_group_key";
export const GROUP_KEY_REQUEST_CLS = "group_key_request";

export interface GroupKeyDistributionMessage {
  cls: "encrypted_group_key";
  group_name: string;
  content: string; // ECDH wire string: IV:ciphertext:signature
}

export interface GroupKeyRequestMessage {
  cls: "group_key_request";
  group_id: number;
}

// Inner JSON payload (matches Android's GroupKeyContentSerializable)
interface GroupKeyPayload {
  group_name: string;
  symmetric_key: string;
  group_owner_username: string;
}

// ---------------------------------------------------------------------------
// Address normalization
// ---------------------------------------------------------------------------

/**
 * Normalizes a group routing address to the canonical base form {"gid":"X","mid":"0"}.
 *
 * Group addresses are per-member JSONs like {"gid":"1","mid":"42"} where mid is the
 * sender's user ID. The symmetric key is keyed by the GROUP (gid), not the sender,
 * so we always strip mid to "0" before any IDB lookup or store. Without this,
 * messages from different senders produce different address strings and the key
 * lookup misses even though the key was stored correctly.
 */
export function normalizeGroupAddress(address: string): string {
  try {
    const parsed = JSON.parse(address);
    if (parsed && typeof parsed.gid !== "undefined") {
      return JSON.stringify({ gid: String(parsed.gid), mid: "0" });
    }
  } catch {
    // not a group JSON — return as-is
  }
  return address;
}

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

export function isGroupKeyDistributionMessage(message: string): boolean {
  try {
    const p = JSON.parse(message);
    return p?.cls === GROUP_KEY_CLS && typeof p.group_name === "string" && typeof p.content === "string";
  } catch {
    return false;
  }
}

export function parseGroupKeyDistributionMessage(
  message: string
): GroupKeyDistributionMessage | null {
  try {
    const p = JSON.parse(message);
    if (p?.cls === GROUP_KEY_CLS && p.group_name && p.content) {
      return p as GroupKeyDistributionMessage;
    }
    return null;
  } catch {
    return null;
  }
}

export function isGroupKeyRequestMessage(message: string): boolean {
  try {
    const p = JSON.parse(message);
    // Require the cls marker AND a numeric group_id (matches parseGroupKeyRequestMessage)
    // so a bare/malformed request isn't misclassified as key exchange.
    return p?.cls === GROUP_KEY_REQUEST_CLS && typeof p.group_id === "number";
  } catch {
    return false;
  }
}

export function parseGroupKeyRequestMessage(message: string): GroupKeyRequestMessage | null {
  try {
    const p = JSON.parse(message);
    if (p?.cls === GROUP_KEY_REQUEST_CLS && typeof p.group_id === "number") {
      return p as GroupKeyRequestMessage;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Owner: generate + distribute
// ---------------------------------------------------------------------------

/**
 * Returns the existing symmetric key for a group, or generates and stores a
 * new one if none exists. Only call this as the group owner.
 */
export async function getOrGenerateGroupSymmetricKey(
  groupAddress: string,
  groupName: string,
  ownerUsername: string
): Promise<string> {
  const canonical = normalizeGroupAddress(groupAddress);
  const existing = await getSymmetricKey(canonical);
  if (existing) return existing.symmetricKeyB64;

  const symmetricKeyB64 = await generateSymmetricKey();
  await storeSymmetricKey(canonical, groupName, ownerUsername, symmetricKeyB64);
  console.log(`[GroupKey] Generated symmetric key for group "${groupName}"`);
  return symmetricKeyB64;
}

/**
 * Creates the JSON string that is sent as a 1:1 message from the owner to
 * one group member, containing the group's symmetric key encrypted for that
 * member using ECDH (same algorithm as regular 1:1 messages).
 */
async function createGroupKeyDistributionContent(
  groupName: string,
  symmetricKeyB64: string,
  ownerUsername: string,
  ownEncPrivKeyJwk: JsonWebKey,
  ownSignPrivKeyJwk: JsonWebKey,
  recipientEncPubKeyJwk: JsonWebKey
): Promise<string> {
  const payload: GroupKeyPayload = {
    group_name: groupName,
    symmetric_key: symmetricKeyB64,
    group_owner_username: ownerUsername,
  };

  const encryptedContent = await encryptMessage(
    JSON.stringify(payload),
    recipientEncPubKeyJwk,
    ownEncPrivKeyJwk,
    ownSignPrivKeyJwk
  );

  const msg: GroupKeyDistributionMessage = {
    cls: GROUP_KEY_CLS,
    group_name: groupName,
    content: encryptedContent,
  };

  return JSON.stringify(msg);
}

/**
 * Sends the group's symmetric key to every member whose public key is in
 * IndexedDB. Members without keys yet are skipped — they'll get the key
 * once they share their own public key and the owner redistributes.
 *
 * @param groupAddress  The group's routing address (used as the key identifier)
 * @param groupName     Human-readable group name
 * @param ownerUsername Current user's username (must be the owner)
 * @param members       All group members (from Group.members)
 * @param userId        Current user's numeric ID
 */
export async function distributeGroupKeyToMembers(
  groupAddress: string,
  groupName: string,
  ownerUsername: string,
  members: GroupMember[],
  userId: number
): Promise<void> {
  if (!E2EE_GROUP_ENABLED) return;

  const ownKeySet = await getActiveKeySet(userId);
  if (!ownKeySet) {
    console.warn("[GroupKey] Cannot distribute — no own key set");
    return;
  }

  const symmetricKeyEntry = await getSymmetricKey(normalizeGroupAddress(groupAddress));
  if (!symmetricKeyEntry) {
    console.warn("[GroupKey] Cannot distribute — no symmetric key stored for group");
    return;
  }

  const { symmetricKeyB64 } = symmetricKeyEntry;

  // Filter to non-owner members (owner already has the key locally)
  const recipients = members.filter((m) => !m.isOwner && m.username !== ownerUsername);

  // Collect each recipient's public encryption key to ECDH-wrap the symmetric key for them.
  // Members without a key yet are skipped — they pull it later via group_key_request.
  const memberKeyMap = new Map<string, PeerPublicKeySet>();
  for (const member of recipients) {
    let peerKeys = await getPublicKeySetByUsername(member.username);
    if (!peerKeys) {
      // Key not in IDB cache — fetch from server. This is expected when the owner
      // is on a fresh browser session (cleared IDB) or the member re-logged in.
      try {
        await requestPublicKey(member.username, userId);
        peerKeys = await getPublicKeySetByUsername(member.username);
      } catch (err) {
        console.debug(`[GroupKey] Could not fetch key for ${member.username}:`, err);
      }
    }
    if (peerKeys) {
      memberKeyMap.set(member.username, peerKeys);
    } else {
      console.debug(`[GroupKey] Skipping ${member.username} — no public key after fetch attempt`);
    }
  }

  // Send distribution to each member who has keys.
  let distributed = 0;
  for (const member of recipients) {
    const peerKeys = memberKeyMap.get(member.username);
    if (!peerKeys) continue;

    try {
      const msgContent = await createGroupKeyDistributionContent(
        groupName,
        symmetricKeyB64,
        ownerUsername,
        ownKeySet.encryptPrivateKeyJwk,
        ownKeySet.signPrivateKeyJwk,
        peerKeys.encryptPublicKeyJwk
      );

      await sendUserMessage({
        cmd: "SendUserMessage",
        message: msgContent,
        address: member.username,
        addressId: 0,
        address_type: USER_ADDRESS_TYPE,
        lat: 0,
        lng: 0,
        media: null,
        media_filename: null,
        media_type: 0,
        sendtime: Date.now(),
        userid: userId,
        // Required: Android's DefaultMessageContentAdapter.responseToContent only
        // parses the inner ECDH-encrypted symmetric key out of `cls:encrypted_group_key`
        // when r_encrypted=1. Without this flag Android non-owners never get the key.
        encrypted: 1,
      });

      distributed++;
      console.log(`[GroupKey] Distributed key for "${groupName}" to ${member.username}`);
    } catch (err) {
      console.error(`[GroupKey] Failed to distribute to ${member.username}:`, err);
    }
  }

  console.log(`[GroupKey] Distributed to ${distributed}/${recipients.length} members`);
}

// ---------------------------------------------------------------------------
// Member: receive + store
// ---------------------------------------------------------------------------

/**
 * Decrypts an incoming group key distribution message and stores the
 * symmetric key in IndexedDB for future group message decryption.
 *
 * @param parsed  The parsed group key distribution message
 * @param userId  Current user's ID (to load own private key)
 * @param ownerUsername  Username of the message sender (the group owner)
 * @returns The canonical group address the key was stored under, or null if it was dropped.
 */
export async function receiveGroupKeyDistributionMessage(
  parsed: GroupKeyDistributionMessage,
  userId: number,
  ownerUsername: string
): Promise<string | null> {
  let [ownKeySet, ownerKeys] = await Promise.all([
    getActiveKeySet(userId),
    getPublicKeySetByUsername(ownerUsername),
  ]);

  if (!ownKeySet) {
    console.warn("[GroupKey] Cannot receive key — no own key set");
    return null;
  }

  // The distribution is ECDH-encrypted: we need the owner's public key to derive
  // the shared secret. If it's missing the distribution arrived before the @keys
  // response — fetch it now and wait (up to the requestPublicKey timeout).
  if (!ownerKeys) {
    console.warn(`[GroupKey] Owner key for "${ownerUsername}" not cached — fetching`);
    try {
      await requestPublicKey(ownerUsername, userId);
      ownerKeys = await getPublicKeySetByUsername(ownerUsername);
    } catch {
      // timeout or network error — fall through to the null guard below
    }
  }

  if (!ownerKeys) {
    console.warn(`[GroupKey] Cannot receive key — no public key for owner "${ownerUsername}" after fetch attempt`);
    return null;
  }

  // Decrypt the ECDH-wrapped payload. If it fails, our cached owner key may be stale (the owner
  // re-keyed, e.g. moved to a new device) — re-fetch it once and retry before giving up. Without
  // this, a stale-but-present owner key loops forever: decrypt fails → drop → re-request → same
  // stale key → fail again. Mirrors the owner-side stale-key re-fetch.
  let payloadJson: string;
  try {
    payloadJson = await decryptMessage(
      parsed.content,
      ownKeySet.encryptPrivateKeyJwk,
      ownerKeys.encryptPublicKeyJwk
    );
  } catch {
    console.warn(`[GroupKey] Decrypt failed with cached key for owner "${ownerUsername}" — re-fetching and retrying`);
    await requestPublicKey(ownerUsername, userId).catch(() => {});
    const freshOwnerKeys = await getPublicKeySetByUsername(ownerUsername);
    if (!freshOwnerKeys) {
      console.error(`[GroupKey] Cannot receive key — no fresh public key for owner "${ownerUsername}"`);
      return null;
    }
    payloadJson = await decryptMessage(
      parsed.content,
      ownKeySet.encryptPrivateKeyJwk,
      freshOwnerKeys.encryptPublicKeyJwk
    );
  }

  const payload: GroupKeyPayload = JSON.parse(payloadJson);

  // Resolve the group's canonical routing address and store the key under it. Force a
  // groups refresh so it's available even right after login. Match by name; use the owner
  // only to disambiguate same-named groups.
  let canonicalAddress: string | null = null;
  try {
    let groups = await getGroups();
    let byName = groups.filter((g) => g.name === payload.group_name);
    if (byName.length === 0) {
      // Group list may be stale (group created/renamed since last fetch) — force refresh.
      groups = await getGroups({ force: true });
      byName = groups.filter((g) => g.name === payload.group_name);
    }
    const ownerMatches = byName.filter(
      (g) => !payload.group_owner_username || g.ownerName === payload.group_owner_username
    );
    if (ownerMatches.length > 1) {
      // Payload has only name + owner (no id), so same-name/same-owner groups are
      // indistinguishable — store under the first; a wrong pick self-heals via re-request.
      console.warn(
        `[GroupKey] Ambiguous group "${payload.group_name}" (owner "${payload.group_owner_username}"): ${ownerMatches.length} groups match — storing under the first.`
      );
    }
    const match = byName.length === 1 ? byName[0] : ownerMatches[0];
    if (match) canonicalAddress = match.address;
  } catch (err) {
    console.warn("[GroupKey] Could not resolve canonical group address", err);
  }

  if (!canonicalAddress) {
    // Don't store under a divergent fallback — that's what caused split-brain. A
    // missing key surfaces as "key not available" and triggers a reactive re-request.
    console.error(
      `[GroupKey] Could not resolve canonical address for group "${payload.group_name}" (owner "${payload.group_owner_username}") — dropping key to avoid split-brain. It will be re-requested.`
    );
    return null;
  }

  const storageKey = normalizeGroupAddress(canonicalAddress);
  // Always overwrite — the owner is authoritative; the latest distribution wins.
  await storeSymmetricKey(
    storageKey,
    payload.group_name,
    payload.group_owner_username,
    payload.symmetric_key
  );

  console.log(
    `[GroupKey] Stored symmetric key for group "${payload.group_name}" from owner "${payload.group_owner_username}" under ${storageKey}`
  );

  return storageKey;
}

/**
 * Returns the symmetric key for a group, keyed by its canonical routing address.
 */
export async function resolveGroupSymmetricKey(
  groupAddress: string
): Promise<string | null> {
  // Normalize to the base address (mid="0") so keys are found regardless of which
  // per-member address variant ({"gid":X,"mid":Y}) the caller passes in.
  const canonical = normalizeGroupAddress(groupAddress);
  return (await getSymmetricKey(canonical))?.symmetricKeyB64 ?? null;
}

/**
 * Sends a group_key_request to the group owner when the current user is a
 * non-owner member who doesn't yet have the symmetric key (or whose key has
 * drifted and can no longer decrypt — the Path B self-heal trigger).
 * Keyed by numeric group id to match Android's GroupKeyRequestRecordDetails (development).
 */
export async function sendGroupKeyRequest(
  ownerUsername: string,
  groupId: number,
  userId: number
): Promise<void> {
  if (!E2EE_GROUP_ENABLED) return;

  const msg = JSON.stringify({ cls: GROUP_KEY_REQUEST_CLS, group_id: groupId });
  await sendUserMessage({
    cmd: "SendUserMessage",
    message: msg,
    address: ownerUsername,
    addressId: 0,
    address_type: USER_ADDRESS_TYPE,
    lat: 0,
    lng: 0,
    media: null,
    media_filename: null,
    media_type: 0,
    sendtime: Date.now(),
    userid: userId,
  });
  console.log(`[GroupKey] Sent group_key_request for group ${groupId} to owner "${ownerUsername}"`);
}

/**
 * When the group owner receives a group_key_request, sends the symmetric key
 * to the requester encrypted for them individually.
 * Matches Android's DefaultGroupKeyExchangeManager.generateRequestedSymmetricKeyMessages().
 *
 * @param requesterUsername  Username of the member requesting the key
 * @param groupAddress       Routing address of the group
 * @param groupName          Human-readable group name
 * @param ownerUsername      Current user's username (must be the group owner)
 * @param userId             Current user's numeric ID
 */
export async function respondToGroupKeyRequest(
  requesterUsername: string,
  groupAddress: string,
  groupName: string,
  ownerUsername: string,
  userId: number
): Promise<void> {
  if (!E2EE_GROUP_ENABLED) return;

  const ownKeySet = await getActiveKeySet(userId);
  if (!ownKeySet) {
    console.warn("[GroupKey] Cannot respond to key request — no own key set");
    return;
  }

  // Check whether the group key already exists before generating. If it doesn't
  // exist, getOrGenerateGroupSymmetricKey creates a NEW key — which means all
  // other web members still have the old key and can't decrypt anything Android
  // sends. In that case, redistribute to every member so everyone stays in sync.
  const canonical = normalizeGroupAddress(groupAddress);
  const existingKey = await getSymmetricKey(canonical);

  const symmetricKeyB64 = await getOrGenerateGroupSymmetricKey(
    groupAddress,
    groupName,
    ownerUsername
  );

  if (!existingKey) {
    // Key was just generated — distribute to all members so web users don't end
    // up with a stale key while Android gets the new one.
    console.log(`[GroupKey] New key generated responding to request from "${requesterUsername}" — redistributing to all members`);
    try {
      const groups = await getGroups();
      const group = groups.find((g) => g.address === groupAddress || g.name === groupName);
      if (group) {
        await distributeGroupKeyToMembers(groupAddress, groupName, ownerUsername, group.members, userId);
        return;
      }
    } catch (err) {
      console.error("[GroupKey] Full redistribution failed, falling through to single-recipient send:", err);
    }
  }

  // Key was pre-existing — send only to the requester (all other members already have it).
  // Always fetch a fresh key from the server — Android regenerates its key pair
  // on every login, so the IDB cache is likely stale. A group_key_request
  // signals the requester's setup changed; using a cached key produces a GCM
  // failure when Android tries to ECDH-decrypt the distribution.
  let peerKeys: PeerPublicKeySet | null = null;
  try {
    console.log(`[GroupKey] Fetching fresh public key for requester "${requesterUsername}"`);
    await requestPublicKey(requesterUsername, userId);
    peerKeys = await getPublicKeySetByUsername(requesterUsername);
  } catch (err) {
    console.warn("[GroupKey] requestPublicKey failed for requester:", err);
  }
  if (!peerKeys) {
    console.warn(`[GroupKey] Cannot respond to key request — no public key for "${requesterUsername}" after fetch attempt`);
    return;
  }

  const msgContent = await createGroupKeyDistributionContent(
    groupName,
    symmetricKeyB64,
    ownerUsername,
    ownKeySet.encryptPrivateKeyJwk,
    ownKeySet.signPrivateKeyJwk,
    peerKeys.encryptPublicKeyJwk
  );

  await sendUserMessage({
    cmd: "SendUserMessage",
    message: msgContent,
    address: requesterUsername,
    addressId: 0,
    address_type: USER_ADDRESS_TYPE,
    lat: 0,
    lng: 0,
    media: null,
    media_filename: null,
    media_type: 0,
    sendtime: Date.now(),
    userid: userId,
    // Same r_encrypted=1 requirement as distributeGroupKeyToMembers — see that
    // function for the Android-side rationale.
    encrypted: 1,
  });
  console.log(`[GroupKey] Responded to key request for "${groupName}" from "${requesterUsername}"`);
}

/**
 * A group_key_request the current user received, reduced to what the owner needs to answer it.
 * `msgId` is the received message's id — used to dedup replays across history reloads.
 */
export interface PendingKeyRequest {
  requesterUsername: string;
  groupId: number;
  msgId: number;
}

/**
 * Owner-side decision + response for a single group_key_request: verifies the current user
 * owns the referenced group and the requester is an actual member, then sends the key.
 * Shared by the live WebSocket path and the history sweep so the ownership/membership gate
 * lives in exactly one place. No-ops (does not throw) when we don't own the group.
 */
export async function answerGroupKeyRequest(params: {
  requesterUsername: string;
  groupId: number;
  ownerUsername: string;
  ownerId: number;
}): Promise<void> {
  const { requesterUsername, groupId, ownerUsername, ownerId } = params;
  const groups = await getGroups();
  const ownedGroup = groups.find((g) => g.ownerName === ownerUsername && g.id === groupId);
  if (!ownedGroup) {
    console.debug(`[GroupKey] Ignoring group_key_request for group ${groupId} — current user is not the owner`);
    return;
  }
  // Only hand the key to actual members — otherwise any authenticated user could obtain any
  // group's key with a forged request. members includes the isOwner-flagged owner too.
  if (!ownedGroup.members.some((m) => m.username === requesterUsername)) {
    console.warn(`[GroupKey] Refusing group_key_request for group ${groupId} — requester "${requesterUsername}" is not a member`);
    return;
  }
  await respondToGroupKeyRequest(requesterUsername, ownedGroup.address, ownedGroup.name, ownerUsername, ownerId);
}

// Requests already answered this session, by received-message id, so re-loading a
// conversation doesn't re-answer the same stored request every time.
const answeredRequestMsgIds = new Set<number>();

/**
 * Answers group_key_requests found in a freshly-loaded conversation history. This closes the
 * gap where a member requested the key while the owner was offline: the request sits in the
 * owner's DM and is answered the next time the owner loads that conversation (the live path
 * only fires when the owner is online at request time). Deduped by msgId; a non-owner load
 * is a cheap no-op (answerGroupKeyRequest bails on non-owned groups). Mirrors Android's
 * owner-side handling of requests that arrive in a message sync.
 */
export async function answerPendingGroupKeyRequests(
  requests: PendingKeyRequest[],
  ownerUsername: string,
  ownerId: number
): Promise<void> {
  await Promise.all(
    requests.map(async (req) => {
      if (answeredRequestMsgIds.has(req.msgId)) return;
      answeredRequestMsgIds.add(req.msgId);
      try {
        await answerGroupKeyRequest({
          requesterUsername: req.requesterUsername,
          groupId: req.groupId,
          ownerUsername,
          ownerId,
        });
      } catch (err) {
        answeredRequestMsgIds.delete(req.msgId); // let a later load retry
        console.error("[GroupKey] Failed to answer pending group_key_request:", err);
      }
    })
  );
}
